<template>
  <section v-if="visible" class="epl">
    <p v-if="loading" class="epl-status">앱 회원정보와 대조하는 중...</p>

    <template v-else-if="!linked">
      <p v-if="errorText" class="epl-error">{{ errorText }}</p>
      <p v-else class="epl-status">연결된 앱 회원이 없습니다.</p>
    </template>

    <template v-else-if="!links.length">
      <p class="epl-ok">앱에 입력한 정보와 직원정보가 같은 항목은 맞춰져 있습니다.</p>
      <p v-for="(warn, i) in warnings" :key="i" class="epl-warn">{{ warn }}</p>
    </template>

    <template v-else>
      <header class="epl-head">
        <h4 class="epl-title">앱 회원정보와 다른 항목</h4>
        <p class="epl-lead">
          같은 항목인데 내용이 다릅니다. 웹과 앱에 함께 쓸 값을 골라 주세요.
          같은 값은 그대로 두었고, 한쪽만 있는 값은 채워 두었습니다.
        </p>
      </header>

      <p v-for="(warn, i) in warnings" :key="`w-${i}`" class="epl-warn">{{ warn }}</p>

      <article v-for="link in links" :key="linkKey(link)" class="epl-block">
        <p v-if="links.length > 1" class="epl-role">{{ link.roleLabel || '앱 회원' }}</p>

        <div v-for="field in link.conflicts" :key="field.key" class="epl-field">
          <p class="epl-label">{{ field.label }}</p>
          <div class="epl-options">
            <button
              type="button"
              class="epl-option"
              :class="{ 'is-on': choiceOf(link, field.key) === 'app' }"
              :aria-pressed="choiceOf(link, field.key) === 'app'"
              @click="pick(link, field.key, 'app')"
            >
              <span class="epl-who">앱에서 입력</span>
              <span class="epl-val">{{ field.appValue }}</span>
            </button>
            <button
              type="button"
              class="epl-option"
              :class="{ 'is-on': choiceOf(link, field.key) === 'web' }"
              :aria-pressed="choiceOf(link, field.key) === 'web'"
              @click="pick(link, field.key, 'web')"
            >
              <span class="epl-who">직원정보에 등록</span>
              <span class="epl-val">{{ field.webValue }}</span>
            </button>
          </div>
          <p v-if="field.hint" class="epl-hint">{{ field.hint }}</p>
        </div>

        <div class="epl-actions">
          <button type="button" class="epl-text" @click="pickLink(link, 'app')">이 회원은 모두 앱 정보</button>
          <button type="button" class="epl-text" @click="pickLink(link, 'web')">이 회원은 모두 직원정보</button>
          <button
            type="button"
            class="epl-save"
            :disabled="!ready(link) || saving"
            @click="save(link)"
          >
            {{ saving ? '저장 중...' : '이 정보로 맞추기' }}
          </button>
        </div>
      </article>
      <p v-if="errorText" class="epl-error">{{ errorText }}</p>
    </template>
  </section>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { employeeApi } from '@/services/api'

type Side = 'app' | 'web'

interface Field {
  key: string
  label: string
  appValue: string
  webValue: string
  hint?: string
}

interface LinkItem {
  role: string
  roleLabel?: string
  centerName?: string
  mCode: string
  clciIdx: number
  clciSource: string
  conflicts: Field[]
}

const props = defineProps<{
  mCode: string
  source: string
  clciIdx: number
}>()

const emit = defineEmits<{
  applied: []
}>()

const loading = ref(false)
const saving = ref(false)
const linked = ref(false)
const links = ref<LinkItem[]>([])
const warnings = ref<string[]>([])
const errorText = ref('')
const choices = reactive<Record<string, Side>>({})

const visible = computed(() => !!props.mCode && !!props.clciIdx && (props.source === 'auto' || props.source === 'manual'))

const linkKey = (link: LinkItem) => `${link.role}:${link.clciSource}:${link.clciIdx}`
const fieldKey = (link: LinkItem, key: string) => `${linkKey(link)}:${key}`
const choiceOf = (link: LinkItem, key: string) => choices[fieldKey(link, key)] || ''

const ready = (link: LinkItem) =>
  link.conflicts.every((field) => choiceOf(link, field.key) === 'app' || choiceOf(link, field.key) === 'web')

const pick = (link: LinkItem, key: string, side: Side) => {
  choices[fieldKey(link, key)] = side
  errorText.value = ''
}

const pickLink = (link: LinkItem, side: Side) => {
  link.conflicts.forEach((field) => {
    choices[fieldKey(link, field.key)] = side
  })
}

const applyPayload = (data: any) => {
  linked.value = data?.linked === true
  links.value = Array.isArray(data?.links) ? data.links : []
  warnings.value = Array.isArray(data?.warnings) ? data.warnings : []
  const alive = new Set<string>()
  links.value.forEach((link) => {
    ;(link.conflicts || []).forEach((field) => alive.add(fieldKey(link, field.key)))
  })
  Object.keys(choices).forEach((key) => {
    if (!alive.has(key)) delete choices[key]
  })
}

const load = async () => {
  if (!visible.value) return
  loading.value = true
  errorText.value = ''
  try {
    const res = await employeeApi.getProfileLink(
      props.mCode,
      props.source === 'manual' ? 'manual' : 'auto',
      props.clciIdx
    )
    applyPayload(res.data?.data)
    if (res.data?.data?.filledCount) emit('applied')
  } catch (error: any) {
    links.value = []
    linked.value = false
    errorText.value = error.response?.data?.message || error.message || '회원정보를 대조하지 못했습니다.'
  } finally {
    loading.value = false
  }
}

const save = async (link: LinkItem) => {
  if (!ready(link) || saving.value) return
  saving.value = true
  errorText.value = ''
  try {
    const choicesBody = Object.fromEntries(link.conflicts.map((field) => [field.key, choiceOf(link, field.key)]))
    const res = await employeeApi.applyProfileLink(
      props.mCode,
      props.source === 'manual' ? 'manual' : 'auto',
      props.clciIdx,
      { role: link.role, choices: choicesBody }
    )
    applyPayload(res.data?.data)
    emit('applied')
  } catch (error: any) {
    errorText.value = error.response?.data?.message || error.message || '저장하지 못했습니다.'
  } finally {
    saving.value = false
  }
}

watch(
  () => `${props.mCode}:${props.source}:${props.clciIdx}`,
  () => {
    links.value = []
    warnings.value = []
    void load()
  },
  { immediate: true }
)
</script>
